import React, { useEffect, useRef, useState } from "react";
import ringtone from "../assets/ringtone.mp3";
import { motion } from "framer-motion";
import { rtdb, auth } from "../firebase";
import { ref, onValue, set, remove } from "firebase/database";
import {
  MdCallEnd,
  MdMic,
  MdMicOff,
  MdVideocam,
  MdVideocamOff,
  MdFlipCameraIos,
  MdPhoneInTalk,
  MdDuo,
  MdFace,
  MdAttachMoney,
  MdPets,
  MdEmojiEmotions,
  MdClose
} from "react-icons/md";

// Standardizing Icon Sizes
const ICON_SIZE = 28;
const END_ICON_SIZE = 36;

export default function VoiceCallOverlay({
  type, // 'incoming' | 'ongoing'
  callerId,
  chatId,
  callType, // 'voice' | 'video' 
  onAccept,
  onReject,
  localStream,
  remoteStream,
  onEnd,
  callStatus,
  onToggleAudio,
  onToggleVideo,
  onSwitchCamera
}) {
  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const ringtoneRef = useRef();

  const [micEnabled, setMicEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);

  // FILTERS
  const [activeFilter, setActiveFilter] = useState(null);
  const [remoteFilter, setRemoteFilter] = useState(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const currentUid = auth.currentUser?.uid;

  // Sync Listener
  useEffect(() => {
    if (!chatId) return;
    const filtersRef = ref(rtdb, `calls/${chatId}/filters`);
    const unsub = onValue(filtersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach(uid => {
          if (uid !== currentUid) {
            setRemoteFilter(data[uid]);
          }
        });
      } else {
        setRemoteFilter(null);
      }
    });
    return () => unsub();
  }, [chatId, currentUid]);

  // Sync Writer
  useEffect(() => {
    if (!chatId || !currentUid) return;
    const myFilterRef = ref(rtdb, `calls/${chatId}/filters/${currentUid}`);
    set(myFilterRef, activeFilter || null);
  }, [activeFilter, chatId, currentUid]);

  // --- Handlers (Moved Up) ---
  const handleToggleAudio = () => {
    const newState = !micEnabled;
    setMicEnabled(newState);
    if (onToggleAudio) onToggleAudio(newState);
  };

  const handleToggleVideo = () => {
    const newState = !videoEnabled;
    setVideoEnabled(newState);
    if (onToggleVideo) onToggleVideo(newState);
  };

  // --- Stream Management (Moved Up) ---
  useEffect(() => {
    if (callStatus === 'ended' || callStatus === 'rejected') return;

    // Force refresh for Local Stream (fix for mobile flip)
    // Force refresh for Local Stream (fix for mobile flip)
    // We detect if the STREAM ID has changed, which happens when we switch cameras
    if (localStream && localVideoRef.current) {
      // Only reset if the object is actually different to avoid flickering loops
      if (localVideoRef.current.srcObject !== localStream) {
        localVideoRef.current.srcObject = localStream;
      }
    }

    if (remoteStream && remoteVideoRef.current) {
      if (remoteVideoRef.current.srcObject !== remoteStream) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
    }
  }, [localStream, remoteStream, callStatus]);

  // --- Ringtone Management (Moved Up) ---
  useEffect(() => {
    if (callStatus === 'ended' || callStatus === 'rejected') {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
      return;
    }

    if (type === "incoming") {
      ringtoneRef.current = new Audio(ringtone);
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(() => {
        // Autoplay blocked by browser policy. Expected behavior until user interaction.
      });
    } else {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    }
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
    };
  }, [type, callStatus]);

  // --- RENDER: STATUS CARD (Ended / Rejected) ---
  if (callStatus === "ended" || callStatus === "rejected") {
    return (
      <div className="fixed inset-0 bg-slate-100/60 backdrop-blur-md z-[60] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-10 flex flex-col items-center gap-6 min-w-[320px] max-w-sm text-center border border-white/50"
        >
          <div className={`w-24 h-24 rounded-full flex items-center justify-center text-5xl shadow-lg ring-4 ring-slate-50 ${callStatus === 'rejected' ? 'bg-red-50 text-red-500' : 'bg-gray-50 text-gray-400'}`}>
            <MdCallEnd />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {callStatus === 'rejected' ? 'Call Rejected' : 'Call Ended'}
            </h2>
            <p className="text-gray-500 text-lg">
              {callStatus === 'rejected' ? 'User is busy.' : 'Session disconnected.'}
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // 🔔 INCOMING CALL VIEW
  if (type === "incoming") {
    return (
      <div className="fixed inset-0 bg-slate-100/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white border border-gray-100 rounded-3xl shadow-2xl p-8 w-full max-w-sm flex flex-col items-center gap-8 relative overflow-hidden ring-1 ring-black/5"
        >
          {/* Background Glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center">
            <div className="w-28 h-28 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg animate-[pulse_2s_infinite]">
              {callType === 'video' ? <MdDuo className="text-white text-5xl" /> : <MdPhoneInTalk className="text-white text-5xl" />}
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mt-6 text-center">{callerId || "Unknown Caller"}</h2>
            <p className="text-gray-500 font-medium mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Incoming {callType === 'video' ? "Video" : "Audio"} Call
            </p>
          </div>

          <div className="flex gap-12 w-full justify-center z-10 pt-4">
            <button
              onClick={onReject}
              className="flex flex-col items-center gap-3 group"
            >
              <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center border border-red-100 transition-all group-hover:bg-red-500 group-hover:text-white group-hover:scale-110 shadow-lg">
                <MdCallEnd size={32} />
              </div>
              <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Decline</span>
            </button>

            <button
              onClick={onAccept}
              className="flex flex-col items-center gap-3 group"
            >
              <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center text-white transition-all group-hover:bg-green-400 group-hover:scale-110 shadow-lg shadow-green-500/30 animate-bounce">
                <MdPhoneInTalk size={32} />
              </div>
              <span className="text-xs text-gray-800 font-bold uppercase tracking-wider">Accept</span>
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // 📞 ONGOING CALL VIEW
  return (
    <div className="fixed inset-0 bg-black z-50 overflow-hidden">

      {/* 1. REMOTE VIDEO (Full Screen Layer) */}
      <div className="absolute inset-0 z-0">
        {remoteStream ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/40 gap-6 bg-gray-900">
            <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
              <MdDuo size={48} />
            </div>
            <p className="text-lg font-medium tracking-wide">Connecting secure line...</p>
          </div>
        )}
        <VideoFilterOverlay type={remoteFilter} />
      </div>

      {/* 2. OVERLAY GRADIENTS (For Visibility) */}
      <div className="absolute inset-0 pointer-events-none z-10 flex flex-col justify-between">
        <div className="h-32 bg-gradient-to-b from-black/80 to-transparent" />
        <div className="h-48 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
      </div>

      {/* 3. HEADER INFO (Top Layer) */}
      <div className="absolute top-0 left-0 w-full p-6 z-20 flex justify-center safe-top">
        <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 flex items-center gap-2 shadow-sm">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-semibold text-white/90 tracking-wide uppercase">End-to-End Encrypted</span>
        </div>
      </div>

      {/* 4. LOCAL VIDEO (PiP) */}
      <motion.div
        drag
        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }} // Restrict drag
        dragElastic={0.1}
        className="absolute top-20 right-4 w-32 h-48 bg-gray-800 rounded-xl overflow-hidden shadow-2xl border border-white/20 z-20"
      >
        {localStream && (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''}`}
            style={{ transform: 'scaleX(-1)' }}
          />
        )}
        {!videoEnabled && (
          <div className="w-full h-full flex items-center justify-center text-white/30 bg-gray-900">
            <MdVideocamOff size={24} />
          </div>
        )}
        <VideoFilterOverlay type={activeFilter} isLocal={true} />
      </motion.div>

      {/* FILTER MENU */}
      {showFilterMenu && (
        <div className="absolute bottom-36 w-full flex justify-center z-40">
          <FilterSelector active={activeFilter} onSelect={(f) => { setActiveFilter(f); setShowFilterMenu(false); }} />
        </div>
      )}

      {/* 5. CONTROLS BAR (Bottom Layer) */}
      <div className="absolute bottom-0 left-0 w-full p-8 pb-12 z-30 flex justify-center items-center gap-6 safe-bottom">

        {/* Filters Toggle */}
        <button
          onClick={() => setShowFilterMenu(!showFilterMenu)}
          className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 transition-all active:scale-95 shadow-lg ${showFilterMenu
            ? 'bg-yellow-400 text-black'
            : 'bg-white/10 text-white hover:bg-white/20'
            }`}
        >
          <MdFace size={ICON_SIZE} />
        </button>

        {/* Mute Toggle */}
        <button
          onClick={handleToggleAudio}
          className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 transition-all active:scale-95 shadow-lg ${micEnabled
            ? 'bg-white/10 text-white hover:bg-white/20'
            : 'bg-white text-gray-900'
            }`}
        >
          {micEnabled ? <MdMic size={ICON_SIZE} /> : <MdMicOff size={ICON_SIZE} />}
        </button>

        {/* End Call (Center) */}
        <button
          onClick={onEnd}
          className="w-20 h-20 rounded-full bg-red-600 text-white flex items-center justify-center shadow-red-600/40 shadow-xl transition-all hover:bg-red-700 active:scale-95"
        >
          <MdCallEnd size={END_ICON_SIZE} />
        </button>

        {/* Video Toggle */}
        <button
          onClick={handleToggleVideo}
          className={`w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 transition-all active:scale-95 shadow-lg ${videoEnabled
            ? 'bg-white/10 text-white hover:bg-white/20'
            : 'bg-white text-gray-900'
            }`}
        >
          {videoEnabled ? <MdVideocam size={ICON_SIZE} /> : <MdVideocamOff size={ICON_SIZE} />}
        </button>

        {/* Flip Camera */}
        <button
          onClick={onSwitchCamera}
          className="w-14 h-14 rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 transition-all active:scale-95 shadow-lg bg-white/10 text-white hover:bg-white/20"
        >
          <MdFlipCameraIos size={ICON_SIZE} />
        </button>

      </div>
    </div>
  );
}

// --- HELPER COMPONENTS ---

function VideoFilterOverlay({ type, isLocal = false }) {
  if (!type) return null;

  return (
    <div className={`absolute inset-0 pointer-events-none z-10 overflow-hidden ${isLocal ? 'rounded-xl' : ''}`}>
      {type === 'money' && <EmojiRain emojis={['💸', '💵', '💰']} />}
      {type === 'emoji' && <EmojiRain emojis={['😂', '😍', '🔥', '🚀', '🎉']} />}
      {type === 'cat' && <FaceAccessory emoji="🐱" color="#FFD54F" />}
      {type === 'dog' && <FaceAccessory emoji="🐶" color="#A1887F" />}
    </div>
  );
}

function FaceAccessory({ emoji }) {
  return (
    <div className="w-full h-full flex justify-center pt-10">
      <div className="text-6xl drop-shadow-lg">{emoji}</div>
    </div>
  );
}

function EmojiRain({ emojis }) {
  const particles = Array.from({ length: 20 });

  return (
    <>
      {particles.map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -50, x: Math.random() * window.innerWidth, opacity: 1 }}
          animate={{ y: window.innerHeight + 50, rotate: 360 }}
          transition={{
            duration: 2 + Math.random() * 3,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "linear"
          }}
          className="absolute text-4xl"
          style={{ left: 0 }}
        >
          {emojis[i % emojis.length]}
        </motion.div>
      ))}
    </>
  );
}

function FilterSelector({ active, onSelect }) {
  const options = [
    { id: null, icon: <MdClose />, label: 'None' },
    { id: 'money', icon: <MdAttachMoney />, label: 'Money' },
    { id: 'cat', icon: <MdPets />, label: 'Cat' },
    { id: 'dog', icon: <MdFace />, label: 'Dog' },
    { id: 'emoji', icon: <MdEmojiEmotions />, label: 'Mix' },
  ];

  return (
    <div className="bg-black/60 backdrop-blur-xl p-3 rounded-2xl flex gap-4 border border-white/10">
      {options.map((opt) => (
        <button
          key={opt.id || 'none'}
          onClick={() => onSelect(opt.id)}
          className={`flex flex-col items-center gap-1 min-w-[50px]`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all ${active === opt.id ? 'bg-green-500 text-white' : 'bg-white/10 text-white'}`}>
            {opt.icon}
          </div>
          <span className={`text-[10px] uppercase font-bold ${active === opt.id ? 'text-green-400' : 'text-gray-400'}`}>{opt.label}</span>
        </button>
      ))}
    </div>
  );
}
